import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import EmailValidator from 'email-validator';
import passwordValidator from 'password-validator';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Grid,
    TextField,
    Typography,
} from '@mui/material';

const base = () => process.env.REACT_APP_BACKEND_ADDRESS;

const validatePassword = (value) => {
    const schema = new passwordValidator();
    schema.is().min(8).has().uppercase().has().lowercase().has().digits().has().symbols().has().not().spaces();
    return schema.validate(value) ? '' : 'Min 8 characters with uppercase, lowercase, number, and symbol.';
};

const SignUp = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    const [tokenState, setTokenState] = useState('checking'); // checking | valid | invalid
    const [prefillEmail, setPrefillEmail] = useState('');

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [username, setUsername] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [loading, setLoading] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [success, setSuccess] = useState(false);

    const [emailError, setEmailError] = useState('');
    const [usernameError, setUsernameError] = useState('');
    const [passwordError, setPasswordError] = useState('');

    useEffect(() => {
        if (!token) { setTokenState('invalid'); return; }
        fetch(`${base()}/invite/validate?token=${token}`)
            .then(r => r.json())
            .then(data => {
                if (data.valid) {
                    setTokenState('valid');
                    if (data.email) {
                        setPrefillEmail(data.email);
                        setEmail(data.email);
                    }
                } else {
                    setTokenState('invalid');
                }
            })
            .catch(() => setTokenState('invalid'));
    }, [token]);

    const handleSubmit = async () => {
        setSubmitError(null);
        setLoading(true);
        try {
            const validateRes = await fetch(`${base()}/validate_signup`, {
                method: 'POST',
                headers: { 'content-type': 'application/json' },
                body: JSON.stringify({ email, username }),
            });
            const validateData = await validateRes.json();
            if (validateData.email) { setEmailError(validateData.email); setLoading(false); return; }
            if (validateData.username) { setUsernameError(validateData.username); setLoading(false); return; }

            const signupRes = await fetch(`${base()}/signup`, {
                method: 'POST',
                headers: { 'content-type': 'application/json' },
                body: JSON.stringify({ email, username, firstName, lastName, password, invite_token: token }),
            });

            if (signupRes.ok) {
                setSuccess(true);
                setTimeout(() => navigate('/login'), 2000);
            } else {
                const err = await signupRes.json();
                setSubmitError(err.detail || 'Sign up failed. Please try again.');
            }
        } catch {
            setSubmitError('Sign up failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const isDisabled = loading || !!emailError || !!usernameError || !!passwordError
        || !email || !username || !password || !firstName || !lastName;

    const pageWrapper = (children) => (
        <Box sx={{ minHeight: '100vh', background: '#f9f4f0', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 3, px: { xs: 2, sm: 0 } }}>
            <Box sx={{ width: '100%', maxWidth: 440 }}>
                {/* Brand header */}
                <Box sx={{ background: '#B61219', borderRadius: '12px 12px 0 0', px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                        component="img"
                        src="/mangiamangia-icon.jpg"
                        alt="Mangia! Mangia!"
                        sx={{ width: { xs: 48, sm: 64 }, height: { xs: 48, sm: 64 }, borderRadius: 1.5, flexShrink: 0, objectFit: 'cover' }}
                    />
                    <Box sx={{ textAlign: 'left', flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontFamily: 'Georgia, serif', fontSize: { xs: 16, sm: 20 }, fontWeight: 'bold', color: '#fff', letterSpacing: 1, lineHeight: 1.2 }}>
                            Sposito Family Cookbook
                        </Typography>
                        <Typography sx={{ fontFamily: 'Georgia, serif', fontSize: 11, color: 'rgba(255,255,255,0.75)', letterSpacing: 2, textTransform: 'uppercase', mt: 0.5 }}>
                            Mangia! Mangia!
                        </Typography>
                    </Box>
                </Box>
                {/* Card body */}
                <Box sx={{ background: '#fff', borderRadius: '0 0 12px 12px', px: 3, py: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                    {children}
                </Box>
            </Box>
        </Box>
    );

    if (tokenState === 'checking') {
        return pageWrapper(
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress sx={{ color: '#B61219' }} />
            </Box>
        );
    }

    if (tokenState === 'invalid') {
        return pageWrapper(
            <Box sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h6" fontWeight={700} gutterBottom>Invitation Required</Typography>
                <Typography color="text.secondary" sx={{ mb: 3 }}>
                    This invite link is invalid or has expired. Ask a family member to send you a new one.
                </Typography>
                <Button variant="outlined" onClick={() => navigate('/login')} sx={{ borderColor: '#B61219', color: '#B61219' }}>
                    Back to Login
                </Button>
            </Box>
        );
    }

    return pageWrapper(
        <>
            <Typography variant="h6" fontWeight={700} gutterBottom>Create your account</Typography>
            <Typography color="text.secondary" sx={{ mb: 2, fontSize: 14 }}>You've been invited to join Mangia! Mangia!</Typography>

            {success ? (
                <Alert severity="success">Account created! Redirecting to login…</Alert>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <TextField
                        fullWidth label="Email" type="email"
                        value={email}
                        onChange={e => { setEmail(e.target.value.trim()); setEmailError(EmailValidator.validate(e.target.value.trim()) ? '' : 'Invalid email format'); }}
                        error={!!emailError} helperText={emailError}
                        disabled={loading || !!prefillEmail}
                        autoComplete="email"
                    />
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <TextField fullWidth label="First Name" value={firstName}
                                onChange={e => setFirstName(e.target.value.trim())} disabled={loading} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField fullWidth label="Last Name" value={lastName}
                                onChange={e => setLastName(e.target.value.trim())} disabled={loading} />
                        </Grid>
                    </Grid>
                    <TextField
                        fullWidth label="Username" value={username}
                        onChange={e => { setUsername(e.target.value.replace(/\s/g, '')); setUsernameError(''); }}
                        error={!!usernameError} helperText={usernameError}
                        disabled={loading} autoComplete="username"
                    />
                    <TextField
                        fullWidth label="Password" type="password" value={password}
                        onChange={e => { setPassword(e.target.value); setPasswordError(validatePassword(e.target.value)); }}
                        error={!!passwordError} helperText={passwordError}
                        disabled={loading} autoComplete="new-password"
                    />
                    {submitError && <Alert severity="error">{submitError}</Alert>}
                    <Button
                        fullWidth variant="contained" size="large"
                        onClick={handleSubmit} disabled={isDisabled}
                        sx={{ mt: 1, background: '#B61219', '&:hover': { background: '#5C0001' }, '&:disabled': { background: '#ccc' } }}
                    >
                        {loading ? <CircularProgress size={22} color="inherit" /> : 'Create Account'}
                    </Button>
                </Box>
            )}
        </>
    );
};

export default SignUp;
